import { defineStore } from 'pinia';
import type { VideoPlatform } from '@/utils/videoSources/types';

interface IVideoSettings {
  addVideoMethod: 'reward' | 'message' | 'bits';
  selectedRewardId: string | null;
  bitsCount: number;
  durationFrom: number;
  durationTo: number;
  viewCount: number;
  queueSize: number;
  banwordsFilter: boolean;
  skipCount: number;
  autoSwitch: boolean;
  enabledPlatforms: VideoPlatform[];
}

interface IVariant {
  name: string;
  words: {
    name: string;
    url?: string;
  }[];
  skipModifier: number;
  color: string;
  permanent?: boolean;
}

interface IStoreState {
  channel: string;
  twitchId: string | null;
  rewardsCache: any[];
  videoSettings: IVideoSettings;
  variantsSettings: IVariant[];
  emotesCache: {
    bttv: any[];
    ffz: any[];
    stv: any[];
  };
  skipPoints: number;
  sfxMuted: boolean;
}

export const useStore = defineStore('store', {
  state: (): IStoreState => ({
    channel: '',
    twitchId: null,
    rewardsCache: [],
    videoSettings: {
      addVideoMethod: 'message',
      selectedRewardId: null,
      bitsCount: 1,
      durationFrom: 0,
      durationTo: 10,
      viewCount: 0,
      queueSize: 20,
      banwordsFilter: true,
      skipCount: 10,
      autoSwitch: true,
      enabledPlatforms: ['youtube', 'tiktok', 'twitch'],
    },
    variantsSettings: [
      {
        name: 'kek',
        skipModifier: -1,
        words: [{ name: 'kek' }, { name: 'кек' }],
        color: '#2a9d8f',
        permanent: true,
      },
      {
        name: 'cringe',
        skipModifier: 1,
        words: [{ name: 'cringe' }, { name: 'кринж' }],
        color: '#e76f51',
        permanent: true,
      },
    ],
    emotesCache: {
      bttv: [],
      ffz: [],
      stv: [],
    },
    skipPoints: 10,
    sfxMuted: false,
  }),

  actions: {
    setChannel(channel: string) {
      this.channel = channel;
    },

    setRewards(rewards: any[]) {
      this.rewardsCache = rewards;
    },

    setTwitchId(twitchId: string | null) {
      this.twitchId = twitchId;
    },

    setVideoSettings(videoSettings: IVideoSettings) {
      this.$patch({ videoSettings });
    },

    setVariantsSettings(variantsSettings: IVariant[]) {
      this.$patch({ variantsSettings });
    },

    setEmotes(emotes: { name: 'bttv' | 'ffz' | 'stv'; value: any[] }) {
      this.emotesCache[emotes.name] = emotes.value;
    },

    save() {
      localStorage['store'] = JSON.stringify(this.$state);
    },

    load() {
      const raw = localStorage['store'];
      if (!raw) return;
      try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          this.$patch(parsed);
        } else {
          localStorage.removeItem('store');
        }
      } catch {
        localStorage.removeItem('store');
      }
    },
  },
});
